/**
 * FinancialSection Component
 *
 * DC-specific financial inputs: CapEx, discount rate, analysis period,
 * escalation rate, PPA rate, carbon credit price.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    color: vortxColors.white,
    '& fieldset': { borderColor: vortxColors.teal },
    '&:hover fieldset': { borderColor: vortxColors.gold },
    '&.Mui-focused fieldset': { borderColor: vortxColors.gold, borderWidth: 2 },
  },
  '& .MuiInputLabel-root': {
    color: vortxColors.stone,
    '&.Mui-focused': { color: vortxColors.gold },
  },
  '& .MuiFormHelperText-root': {
    color: vortxColors.stone,
  },
  '& .MuiInputAdornment-root': {
    color: vortxColors.gold,
  },
};

function FinancialSection({ project, onUpdate }) {
  const [formData, setFormData] = useState({
    total_budget: '',
    discount_rate: '8.0',
    analysis_period_years: '20',
    escalation_rate: '2.5',
    ppa_rate: '',
    carbon_credit_price: '',
    cost_of_downtime_per_hour: '',
  });

  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (project) {
      setFormData({
        total_budget: project.total_budget || '',
        discount_rate: project.discount_rate || '8.0',
        analysis_period_years: project.analysis_period_years || '20',
        escalation_rate: project.escalation_rate || '2.5',
        ppa_rate: project.ppa_rate || '',
        carbon_credit_price: project.carbon_credit_price || '',
        cost_of_downtime_per_hour: project.cost_of_downtime_per_hour || '',
      });
    }
  }, [project]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(formData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save financial info:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontFamily: vortxTypography.heading,
          color: vortxColors.white,
          mb: 3,
        }}
      >
        Financial Parameters
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Total Budget"
            type="number"
            value={formData.total_budget}
            onChange={handleChange('total_budget')}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            helperText="Maximum capital expenditure for energy infrastructure"
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Discount Rate"
            type="number"
            value={formData.discount_rate}
            onChange={handleChange('discount_rate')}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            helperText="Weighted average cost of capital (WACC)"
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Analysis Period"
            type="number"
            value={formData.analysis_period_years}
            onChange={handleChange('analysis_period_years')}
            InputProps={{
              endAdornment: <InputAdornment position="end">years</InputAdornment>,
            }}
            helperText="Economic analysis period (typically 15-25 years)"
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Escalation Rate"
            type="number"
            value={formData.escalation_rate}
            onChange={handleChange('escalation_rate')}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            helperText="Annual utility rate escalation"
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="PPA Rate"
            type="number"
            value={formData.ppa_rate}
            onChange={handleChange('ppa_rate')}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              endAdornment: <InputAdornment position="end">/kWh</InputAdornment>,
            }}
            helperText="Power purchase agreement rate (if applicable)"
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Carbon Credit Price"
            type="number"
            value={formData.carbon_credit_price}
            onChange={handleChange('carbon_credit_price')}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              endAdornment: <InputAdornment position="end">/ton</InputAdornment>,
            }}
            helperText="Carbon credit or offset price"
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Cost of Downtime"
            type="number"
            value={formData.cost_of_downtime_per_hour}
            onChange={handleChange('cost_of_downtime_per_hour')}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              endAdornment: <InputAdornment position="end">/hr</InputAdornment>,
            }}
            helperText="Revenue loss per hour of unplanned downtime"
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} sx={{ color: '#000' }} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                background: `linear-gradient(135deg, ${vortxColors.gold} 0%, #d4a574 100%)`,
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  background: `linear-gradient(135deg, #d4a574 0%, ${vortxColors.gold} 100%)`,
                },
              }}
            >
              Save Changes
            </Button>
            {saveSuccess && <Alert severity="success">Changes saved successfully!</Alert>}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default FinancialSection;
